/**
 * Customers API Client
 * Typed API functions for customer operations
 */

import { Customer } from '@shared/schema';
import { PaginatedResponse, PaginationParams } from '@/lib/pagination/types';

export interface CustomerFilters {
  search?: string;
  status?: string;
  pipelineStageId?: number;
}

/**
 * Fetch paginated customers from the API
 */
export async function fetchCustomers(
  params: PaginationParams & CustomerFilters
): Promise<PaginatedResponse<Customer>> {
  const { page, limit, search, status, pipelineStageId } = params;

  // Build query string
  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });

  if (search) queryParams.set('search', search);
  if (status && status !== 'all') queryParams.set('status', status);
  if (pipelineStageId) queryParams.set('pipelineStageId', pipelineStageId.toString());

  const response = await fetch(`/api/customers?${queryParams}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch customers: ${response.statusText}`);
  }

  const data = await response.json();

  // Handle both old and new response formats
  if (Array.isArray(data)) {
    // Old format - convert to paginated response
    return {
      data: data,
      pagination: {
        page: 1,
        limit: data.length,
        totalPages: 1,
        totalCount: data.length,
        hasNext: false,
        hasPrevious: false,
      },
    };
  }

  // New paginated format
  return data;
}

/**
 * Fetch a single customer by ID
 */
export async function fetchCustomer(id: number): Promise<Customer> {
  const response = await fetch(`/api/customers/${id}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch customer: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Create a new customer
 */
export async function createCustomer(data: Partial<Customer>): Promise<Customer> {
  const response = await fetch('/api/customers', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to create customer: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Update an existing customer
 */
export async function updateCustomer(id: number, data: Partial<Customer>): Promise<Customer> {
  const response = await fetch(`/api/customers/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to update customer: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Delete a customer
 */
export async function deleteCustomer(id: number): Promise<void> {
  const response = await fetch(`/api/customers/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to delete customer: ${response.statusText}`);
  }
}
